import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Sparkles } from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import JobMatchModal from "./JobMatchModal";
import AdaptCVModal from "./AdaptCVModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobMatchCard({ jobId, jobTitle, companyName }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [state, setState] = useState("loading"); // loading | no_cv | ready | error
  const [analysis, setAnalysis] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAdaptModal, setShowAdaptModal] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "candidate") return;
    let active = true;
    axios.get(`${API}/jobs/${jobId}/match`, { headers: getAuthHeaders() })
      .then((res) => { if (active) { setAnalysis(res.data); setState("ready"); } })
      .catch((err) => { if (active) setState(err.response?.status === 404 ? "no_cv" : "error"); });
    return () => { active = false; };
  }, [jobId, user]);

  if (!user || user.role !== "candidate" || state === "error") return null;

  if (state === "loading") {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-center h-24" data-testid="match-card-loading">
        <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
      </div>
    );
  }

  if (state === "no_cv") {
    return (
      <div className="bg-white border border-dashed border-[#028CF8]/30 rounded-2xl p-5" data-testid="match-card-no-cv">
        <p className="text-sm font-bold text-slate-900 mb-1.5">{t("jobMatch.noCvTitle")}</p>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">{t("jobMatch.noCvDesc")}</p>
        <Link to="/cv-builder" data-testid="match-create-cv-link"
          className="w-full h-10 flex items-center justify-center bg-[#028CF8] text-white text-sm font-semibold rounded-xl hover:bg-[#0270C5] transition-all">
          {t("jobMatch.createCvCta")}
        </Link>
      </div>
    );
  }

  const score = analysis.score;
  const color = score >= 70 ? "#028CF8" : score >= 45 ? "#F59E0B" : "#64748B";
  const circumference = 2 * Math.PI * 15.5;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="match-card">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3">{t("jobMatch.cardTitle")}</p>
        <div className="flex items-center gap-4 mb-4">
          <div className="relative h-16 w-16 flex-shrink-0">
            <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#F1F5F9" strokeWidth="3.5" />
              <circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3.5"
                strokeDasharray={`${(score / 100) * circumference} ${circumference}`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">{score}%</span>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900" data-testid="match-score-text">{score}% {t("jobMatch.compatible")}</p>
            <p className="text-xs text-slate-500">{t("jobMatch.basedOnCv")}</p>
          </div>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="match-view-analysis-btn"
          className="w-full h-10 border border-[#028CF8]/30 text-[#028CF8] text-sm font-semibold rounded-xl hover:bg-[#028CF8]/5 transition-all">
          {t("jobMatch.viewAnalysis")}
        </button>
        <button onClick={() => setShowAdaptModal(true)} data-testid="adapt-cv-cta-btn"
          className="w-full h-10 mt-2 flex items-center justify-center gap-1.5 text-[#F55FA5] text-sm font-semibold rounded-xl hover:bg-[#F55FA5]/5 transition-all">
          <Sparkles className="h-3.5 w-3.5" /> {t("jobMatch.adaptCvCta")}
        </button>
      </div>
      {showModal && <JobMatchModal analysis={analysis} onClose={() => setShowModal(false)} />}
      {showAdaptModal && (
        <AdaptCVModal jobId={jobId} jobTitle={jobTitle} companyName={companyName} onClose={() => setShowAdaptModal(false)} />
      )}
    </>
  );
}
